import { Preferences } from "@capacitor/preferences";
import * as Sentry from "@sentry/browser";

import {
  DEVICE_REGISTRATION_LIMIT_ERROR_CATEGORY,
  getTransientPushRegistrationErrorCategory,
  isDeviceRegistrationLimitError,
  isNotificationsDisallowedError,
  TRANSIENT_PUSH_REGISTRATION_ERROR_CATEGORIES,
} from "./pushNotificationErrors";

const STORAGE_KEY = "PUSH_REGISTRATION_MONITOR_V1";
const REPORT_INTERVAL_MS = 24 * 60 * 60 * 1000;
const WARNING_THRESHOLD = 3;
// 永続化キー（`<category>:<level>`）の検証用。
// カテゴリ一覧は pushNotificationErrors から取り込み、分類を追加したときにここがずれないようにする。
const LAST_REPORTED_KEY_PATTERN = new RegExp(
  `^(${[...TRANSIENT_PUSH_REGISTRATION_ERROR_CATEGORIES, DEVICE_REGISTRATION_LIMIT_ERROR_CATEGORY].join("|")}):(info|warning)$`
);

type ReportLevel = "info" | "warning";
type State = {
  consecutiveFailures: number;
  firstFailureAt: number | null;
  lastReported: Record<string, number>;
};

let state: State = { consecutiveFailures: 0, firstFailureAt: null, lastReported: {} };
let loaded = false;
let pending = Promise.resolve();

const isTimestamp = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const loadState = async () => {
  if (loaded) return;
  loaded = true;
  try {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return;
    const saved = JSON.parse(value);
    if (
      !saved ||
      !Number.isSafeInteger(saved.consecutiveFailures) ||
      saved.consecutiveFailures < 0 ||
      (saved.consecutiveFailures > 0 && !isTimestamp(saved.firstFailureAt)) ||
      !saved.lastReported ||
      typeof saved.lastReported !== "object" ||
      Array.isArray(saved.lastReported)
    ) return;

    const lastReported: Record<string, number> = {};
    for (const [key, timestamp] of Object.entries(saved.lastReported)) {
      if (LAST_REPORTED_KEY_PATTERN.test(key) && isTimestamp(timestamp)) {
        lastReported[key] = timestamp;
      }
    }
    state = {
      consecutiveFailures: saved.consecutiveFailures,
      firstFailureAt: saved.consecutiveFailures > 0 ? saved.firstFailureAt : null,
      lastReported,
    };
  } catch {
    // 保存領域が利用できなくても、同一実行中の集計・送信制限はメモリで継続する。
    console.info("プッシュ通知登録の監視状態を読み込めませんでした");
  }
};

// Preferences の読み書きを直列化し、失敗と成功の到着順を維持する。
const enqueue = (update: () => void): Promise<void> => {
  pending = pending.then(async () => {
    await loadState();
    update();
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(state) });
  }).catch(() => {
    // 監視処理の失敗でプッシュ登録を妨げたり、未処理の Promise rejection を発生させない。
    console.info("プッシュ通知登録の監視状態を保存できませんでした");
  });
  return pending;
};

export const recordPushRegistrationFailure = (error: unknown, platform: string): Promise<void> => {
  if (isNotificationsDisallowedError(error)) return Promise.resolve();
  const registrationLimit = isDeviceRegistrationLimitError(error);
  const category = registrationLimit
    ? DEVICE_REGISTRATION_LIMIT_ERROR_CATEGORY
    : getTransientPushRegistrationErrorCategory(error);
  if (!category) return Promise.resolve();
  const now = Date.now();

  return enqueue(() => {
    // 登録数上限は独立した warning とし、一時的失敗の連続回数には含めない。
    if (!registrationLimit) {
      state.consecutiveFailures = Math.min(state.consecutiveFailures + 1, Number.MAX_SAFE_INTEGER);
      state.firstFailureAt = Math.min(state.firstFailureAt ?? now, now);
    }

    const client = Sentry.getCurrentHub().getClient();
    // 未初期化・無効の Sentry に送信済みとして記録しない。
    if (!client || client.getOptions().enabled === false) return;

    const report = (level: ReportLevel) => {
      const key = `${category}:${level}`;
      const lastReported = state.lastReported[key];
      if (lastReported !== undefined && now >= lastReported && now - lastReported < REPORT_INTERVAL_MS) return;

      try {
        Sentry.withScope((scope) => {
          // 過去の console breadcrumb 等に含まれるトークン・ユーザー情報をこのイベントに引き継がない。
          scope.clear();
          scope.setTags({ feature: "push_registration", push_error_category: category, push_platform: platform });
          if (!registrationLimit) {
            scope.setExtras({
              consecutive_failures: state.consecutiveFailures,
              failure_duration_ms: Math.max(0, now - (state.firstFailureAt ?? now)),
            });
          }
          // SDK の Dedupe は level を比較しないため、info と warning を別 fingerprint にする。
          scope.setFingerprint(["push_registration", category, platform, level]);
          Sentry.captureMessage(
            "Push notification registration failed",
            level === "warning" ? Sentry.Severity.Warning : Sentry.Severity.Info
          );
        });
        state.lastReported[key] = now;
      } catch {
        console.info("プッシュ通知登録の監視イベントを送信できませんでした");
      }
    };

    if (registrationLimit) {
      report("warning");
      return;
    }
    report("info");
    if (state.consecutiveFailures >= WARNING_THRESHOLD) report("warning");
  });
};

// register() の resolve では呼ばない。有効な registration トークンの受信時のみ成功とする。
export const recordPushRegistrationSuccess = (): Promise<void> => enqueue(() => {
  state.consecutiveFailures = 0;
  state.firstFailureAt = null;
  // 成功後の再失敗でも24時間以内に大量送信しないよう、送信時刻は保持する。
});
